import type { Notifier } from '@fleece/models';
import { Save } from 'lucide-react';
import { useState } from 'react';
import { Modal } from '@/components/modal';
import { DialogForm } from '@/components/settings/credential-dialogs';
import { Button, Field, Input, Spinner } from '@/components/ui';
import { useCreateNotifier, useUpdateNotifier } from '@/hooks/use-settings';
import { newNotifierDraft, notifierCreateRequest, notifierDraftOf, notifierDraftProblem, notifierUpdateRequest, type NotifierDraft } from '@/lib/settings';

export interface NotifierDialogProps {
  readonly open: boolean;
  readonly editing?: Notifier;
  /** A name to start a new notifier with, such as the one the position monitor expects. */
  readonly suggestedName?: string;
  readonly onClose: () => void;
}

export function NotifierDialog({ open, editing, suggestedName, onClose }: NotifierDialogProps) {
  return (
    <Modal open={open} title={editing === undefined ? 'Add Discord notifier' : `Edit ${editing.name}`} onClose={onClose} className="w-[min(420px,calc(100vw-32px))]">
      <NotifierForm editing={editing} suggestedName={suggestedName} onDone={onClose} />
    </Modal>
  );
}

function NotifierForm({ editing, suggestedName, onDone }: { editing?: Notifier; suggestedName?: string; onDone: () => void }) {
  const [draft, setDraft] = useState<NotifierDraft>(() => (editing === undefined ? { ...newNotifierDraft(), name: suggestedName ?? '' } : notifierDraftOf(editing)));
  const create = useCreateNotifier();
  const update = useUpdateNotifier();
  const write = editing === undefined ? create : update;
  const problem = notifierDraftProblem(draft, editing !== undefined);
  const set = (change: Partial<NotifierDraft>): void => setDraft((current) => ({ ...current, ...change }));

  const submit = (): void => {
    if (problem !== undefined) {
      return;
    }
    if (editing === undefined) {
      create.mutate(notifierCreateRequest(draft), { onSuccess: onDone });
    } else {
      update.mutate(notifierUpdateRequest(editing, draft), { onSuccess: onDone });
    }
  };

  // Shown only once something has been typed, so an empty form does not open scolding.
  const shownProblem = draft.webhookUrl.trim().length > 0 ? problem : undefined;

  return (
    <DialogForm
      onSubmit={submit}
      error={write.error?.message ?? shownProblem}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={problem !== undefined || write.isPending}>
            {write.isPending ? <Spinner className="border-accent-ink/40 border-t-accent-ink" /> : <Save className="size-3.5" />}
            Save
          </Button>
        </>
      }
    >
      <Field label="Name" hint="the position monitor posts to “monitor” and “attention”">
        <Input value={draft.name} onChange={(event) => set({ name: event.target.value })} autoFocus={editing === undefined && suggestedName === undefined} />
      </Field>
      <Field label="Channel" hint="as you call it">
        <Input value={draft.channel} onChange={(event) => set({ channel: event.target.value })} placeholder="#fleece-alerts" autoFocus={suggestedName !== undefined} />
      </Field>
      <Field label="Webhook URL" hint={editing === undefined ? 'Integrations → Webhooks' : 'leave blank to keep the current one'}>
        <Input
          type="password"
          value={draft.webhookUrl}
          onChange={(event) => set({ webhookUrl: event.target.value })}
          placeholder={editing === undefined ? 'https://discord.com/api/webhooks/…' : `current webhook ${editing.target.webhookId}`}
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
        />
      </Field>
      <Field label="Description" hint="optional">
        <Input value={draft.description} onChange={(event) => set({ description: event.target.value })} placeholder="Signals only; notifies" />
      </Field>
    </DialogForm>
  );
}
